import { GRAMMAR, OWL_LINK_SOURCE, PLAGIARISM } from '@src/components/Cards/constants'
import { OwlLinkAdditionalData } from '@src/components/Cards/types'
import { NAV_SIGNIN, NAV_SIGNUP } from '@src/components/Header/SignupAndLogin'
import { PLAGIARISM_LINK_SOURCE } from '@src/components/Modals/PlagiarismSourcesModal/PlagiarismSource'
import { PLAGIARISM_LOCK } from '@src/components/PlagiarismLock'
import {
  UPGRADE_TO_EASBIB_STUDY_PLUS,
  UPGRADE_TO_EASYBIB_PLUS,
} from '@src/components/PricingTable'
import { GRAMMAR_LOCK } from '@src/layouts/shared/BlurredCard'
import {
  FREE_GRAMMAR_EMPTY_STATE,
  GRAMMAR_MIDPANEL_PLAGIARISM_CHECK,
  PLAGIARISM_CHECK_MIDPANEL,
  PLAGIARISM_RECHECK_MIDPANEL,
} from '@src/layouts/shared/EmptyStates/constants'
import {
  MORE_ACTIONS_MENU_CREATE_ACCOUNT,
  MORE_ACTIONS_MENU_SAVE,
} from '@src/layouts/shared/MoreActions/Menu'
import {
  RECHECK_MENU_GRAMMAR,
  RECHECK_MENU_PLAGIARISM,
  UPGRADE_TO_RECHECK,
} from '@src/layouts/shared/Recheck'
import { PAPERS_SECTION_SIGNIN } from '@src/pages/MyPapers/PapersSection'
import { PAGE_TYPES } from '@src/state/analytics/constants'
import {
  handleFreshToken,
  LogoutMeta,
  ShowCheggAuthExperience,
} from '@src/state/auth/actions'
import { getPaidMember } from '@src/state/auth/selectors'
import {
  CardActionsMeta,
  CardActionsPayload,
  CheckMeta,
  CheckPayloadBase,
  CheckResultsError,
} from '@src/state/checks/actions'
import {
  ActiveEntityKeyMeta,
  ClickNavLinkPayload,
  ExternalLinkMeta,
} from '@src/state/document/actions'
import {
  createTicketFailure,
  createTicketSuccess,
  EXPERT_CHECK_CREATE_SUCCESS,
} from '@src/state/expertcheck/actions'
import {
  getExpertCheckTicketId,
  selectZohoCookie,
} from '@src/state/expertcheck/selectors'
import { eCVirtualPageMap, getFreemiumPlagData, getGTMData } from '@src/state/gtm/helper'
import {
  ModalPropsPayloads,
  PAPER_UPLOADED_MODAL,
  PAPER_UPLOADED_NON_PLUS_MODAL,
  PLAGIARISM_CHECK_MODAL,
  PLAGIARISM_SOURCES_MODAL,
  REVIEWING_PAPER_MODAL,
  showModal,
} from '@src/state/modals/actions'
import { handleFreeTrialStart } from '@src/state/payment/actions'
import { EN_GB } from '@src/state/session/types'
import { RootState } from '@src/state/types'
import {
  ClickstreamInteractionPayload,
  EXPERT_CHECK_CTA_ELEMENT,
  GRAMMAR_CARD_CTA_ORIGIN,
  UNSUPPORTED_CLAIMS_EC_ORIGIN,
} from '@src/state/userevents/actions'
import {
  SetCurrentCategoryPayload,
  SwitchLocalePayload,
} from '@src/state/workspace/actions'
import {
  EC_FORM_PAGE_ID,
  EC_PAGE_ID,
  EC_UPGRADE_PAGE_ID,
  EXPERT_CHECK_TOGGLE,
} from '@src/utils/dataTestIds'
import { isECFormPage, isRootPage, isWorkspacePage } from '@src/utils/routes'
import { RouterState } from 'connected-react-router'
import { EventDefinition } from 'redux-beacon'
import { ActionType } from 'typesafe-actions'
import {
  EVENT_ACTIONS,
  EVENT_CATEGORY,
  EVENT_LABELS,
  EVENT_TYPES,
  VIRTUALPAGEPATHS,
} from './constants'

export const expertCheckTicketEvent: EventDefinition = (
  action: ActionType<typeof createTicketSuccess | typeof createTicketFailure>,
  prevState: RootState,
  nextState: RootState
) => {
  const eventCategory = EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS
  const eventAction = EVENT_ACTIONS.EXPERT_CHECK
  let eventLabel = EVENT_LABELS.EXPERT_CHECK_SUBMIT_ERROR
  if (action.type === EXPERT_CHECK_CREATE_SUCCESS) {
    eventLabel = EVENT_LABELS.EXPERT_CHECK_SUBMIT_SUCCESS
  }
  return {
    event: EVENT_TYPES.EVENT,
    eventCategory,
    eventAction,
    eventLabel,
    ecFeedbackType: action.payload.feedbackTypes.join(','),
    ...getGTMData(prevState),
  }
}
export const showModalEvent: EventDefinition = (
  action: ActionType<typeof showModal>,
  prevState: RootState,
  nextState: RootState
) => {
  const { name } = action.payload
  let virtualPagePath
  switch (name) {
    case PLAGIARISM_SOURCES_MODAL:
      virtualPagePath = VIRTUALPAGEPATHS.PLAGIARISM_SOURCES
      break
    case PLAGIARISM_CHECK_MODAL:
      virtualPagePath = VIRTUALPAGEPATHS.PLAGIARISM_CHECK
      break
    case REVIEWING_PAPER_MODAL:
      if (
        action.payload.modalProps &&
        (action.payload.modalProps as ModalPropsPayloads[typeof name]).isRecheck
      ) {
        virtualPagePath = VIRTUALPAGEPATHS.GRAMMAR_CHECK
      } else {
        return
      }
      break
    case PAPER_UPLOADED_NON_PLUS_MODAL:
    case PAPER_UPLOADED_MODAL:
      virtualPagePath = VIRTUALPAGEPATHS.PAPER_PROCESSED
      break
    default:
      return
  }
  if (virtualPagePath === VIRTUALPAGEPATHS.PAPER_PROCESSED) {
    return {
      event: EVENT_TYPES.VIRTUALPAGE,
      virtualPagePath,
      ...getGTMData(prevState),
      ...getFreemiumPlagData(prevState),
    }
  }
  return {
    event: EVENT_TYPES.VIRTUALPAGE,
    virtualPagePath,
    ...getGTMData(prevState),
  }
}

export const authModalEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { experience } = action.payload as ShowCheggAuthExperience
  let virtualPagePath = VIRTUALPAGEPATHS.SIGN_IN
  if (experience === 'signup') {
    virtualPagePath = VIRTUALPAGEPATHS.SIGN_UP
  }
  return {
    event: EVENT_TYPES.VIRTUALPAGE,
    virtualPagePath,
    ...getGTMData(prevState),
  }
}

export const accountCtaEvents: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  let eventCategory
  let eventLabel
  let eventAction
  switch (action.payload.origin) {
    case NAV_SIGNIN:
      eventCategory = EVENT_CATEGORY.NAVIGATION_BAR
      eventLabel = EVENT_LABELS.SIGN_IN
      eventAction = EVENT_ACTIONS.SIGNIN_CTA
      break
    case NAV_SIGNUP:
      eventCategory = EVENT_CATEGORY.NAVIGATION_BAR
      eventLabel = EVENT_LABELS.CREATE_ACCOUNT
      eventAction = EVENT_ACTIONS.SIGNUP_FOR_PLUS_CTA
      break
    case PAPERS_SECTION_SIGNIN:
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_FUNNEL
      eventLabel = EVENT_LABELS.MY_PAPERS
      eventAction = EVENT_ACTIONS.SIGNIN_CTA
      break
    case PAPER_UPLOADED_NON_PLUS_MODAL:
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_FUNNEL
      eventLabel = EVENT_LABELS.UPSELL_MODAL_LINK
      eventAction = EVENT_ACTIONS.SIGNIN_CTA
      break
    default:
      return
  }
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory,
    eventLabel,
    eventAction,
  }
}

export const logoutEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { isUserAction } = action.meta as LogoutMeta
  if (!isUserAction) {
    return
  }
  return {
    event: EVENT_TYPES.EVENT,
    eventAction: EVENT_ACTIONS.LOGOUT,
    eventLabel: EVENT_LABELS.HEADER_NAVIGATION,
    eventCategory: EVENT_CATEGORY.ACCOUNT,
    ...getGTMData(prevState),
  }
}

export const loginEvent: EventDefinition = (
  action: ActionType<typeof handleFreshToken>,
  prevState: RootState,
  nextState: RootState
) => {
  const { loginAuthType } = action.meta
  const eventAction =
    loginAuthType === EVENT_ACTIONS.LOGIN ? EVENT_ACTIONS.LOGIN : EVENT_ACTIONS.SIGN_UP

  if (loginAuthType) {
    return {
      eventAction,
      event: EVENT_TYPES.EVENT,
      eventCategory: EVENT_CATEGORY.ACCOUNT,
      eventLabel: EVENT_LABELS.SUCCESS,
      ...getGTMData(nextState),
    }
  }
  return
}

export const locationChangeEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { pathname, state } = (action.payload as RouterState).location
  const commonData = getGTMData(nextState)
  if (isECFormPage(pathname)) {
    if (state && state.from === EXPERT_CHECK_TOGGLE) {
      // if form page, we need to send a form virtual page view + nav link click event
      if (
        getPaidMember(nextState) &&
        !getExpertCheckTicketId(nextState) &&
        !selectZohoCookie(nextState)
      ) {
        return [
          {
            event: EVENT_TYPES.EVENT,
            eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
            eventAction: EVENT_ACTIONS.EXPERT_CHECK,
            eventLabel: EVENT_LABELS.NAV_LINK,
            ...commonData,
          },
          {
            event: EVENT_TYPES.VIRTUALPAGE,
            virtualPagePath: eCVirtualPageMap[commonData.pageSubGroup],
            ...commonData,
          },
        ]
      } else {
        return {
          event: EVENT_TYPES.EVENT,
          eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
          eventAction: EVENT_ACTIONS.EXPERT_CHECK,
          eventLabel: EVENT_LABELS.NAV_LINK,
          ...commonData,
        }
      }
    } else if (
      state &&
      (state.from === EC_PAGE_ID ||
        // capture page view for upgrade page -> form page (user subscribes)
        state.from === EC_UPGRADE_PAGE_ID)
    ) {
      return {
        event: EVENT_TYPES.VIRTUALPAGE,
        virtualPagePath: eCVirtualPageMap[commonData.pageSubGroup],
        ...commonData,
      }
    }
  }

  if (isRootPage(pathname)) {
    let eventAction = EVENT_ACTIONS.MY_PAPERS
    let eventLabel = EVENT_LABELS.HEADER_LINKS
    if (state && state.helperText) {
      eventAction = state.helperText
      eventLabel = EVENT_LABELS.NAV_LINK
    } else if (state && state.from === EC_FORM_PAGE_ID) {
      eventAction = EVENT_ACTIONS.EXPERT_CHECK
      eventLabel = EVENT_LABELS.EXPERT_CHECK_UPLOAD
    }
    return {
      event: EVENT_TYPES.EVENT,
      eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
      eventLabel,
      eventAction,
      ...commonData,
    }
  } else if (isWorkspacePage(pathname)) {
    if (state && state.from === PAGE_TYPES.PAPERS) {
      return {
        event: EVENT_TYPES.EVENT,
        eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
        eventAction: EVENT_ACTIONS.OPEN_PREVIOUS_PAPER,
        eventLabel: EVENT_LABELS.MY_PAPERS_PAGE,
        ...commonData,
      }
    } else if (state && state.from === EC_FORM_PAGE_ID) {
      return {
        event: EVENT_TYPES.EVENT,
        eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
        eventAction: EVENT_ACTIONS.EXPERT_CHECK,
        eventLabel: EVENT_LABELS.EXPERT_CHECK_EDIT,
        ...commonData,
      }
    }
  }
  return
}

export const runChecksEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { origin } = action.meta as CheckMeta

  if (!origin) {
    return
  }

  let eventAction
  let eventCategory
  let eventLabel

  switch (origin) {
    case RECHECK_MENU_GRAMMAR: {
      eventAction = EVENT_ACTIONS.RECHECK_GRAMMAR
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS
      eventLabel = EVENT_LABELS.NAV_LINK
      break
    }
    default: {
      eventAction = origin
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_FUNNEL
      eventLabel = EVENT_LABELS.UNLOCKED_CARD
    }
  }
  return {
    event: EVENT_TYPES.EVENT,
    eventAction,
    eventCategory,
    eventLabel,
    ...getGTMData(prevState),
  }
}

export const plagiarismCheckEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { type } = action.payload as CheckPayloadBase
  if (type === 'plagiarism') {
    const { origin } = action.meta
    let eventLabel
    let eventAction = EVENT_ACTIONS.CHECK_FOR_PLAGIARISM
    switch (origin) {
      case PLAGIARISM_CHECK_MIDPANEL: {
        eventLabel = EVENT_LABELS.PRECHECK_PLAG_EMPTY_STATE
        break
      }
      case GRAMMAR_MIDPANEL_PLAGIARISM_CHECK: {
        eventLabel = EVENT_LABELS.PLUS_GRAMMAR_EMPTY_STATE
        break
      }
      case PLAGIARISM_RECHECK_MIDPANEL: {
        eventLabel = EVENT_LABELS.PLAGIARISM_EMPTY_STATE
        break
      }
      case RECHECK_MENU_PLAGIARISM: {
        eventLabel = EVENT_LABELS.NAV_LINK
        eventAction = EVENT_ACTIONS.RECHECK_PLAGIARISM
        break
      }
      default: {
        eventLabel = EVENT_LABELS.UNLOCKED_CARD
      }
    }

    return {
      event: EVENT_TYPES.EVENT,
      eventAction,
      eventCategory: EVENT_CATEGORY.PAPERCHECKER_FUNNEL,
      eventLabel,
      ...getGTMData(prevState),
    }
  }
  return
}

export const handleFreeTrialStartEvent: EventDefinition = (
  action: ActionType<typeof handleFreeTrialStart>,
  prevState: RootState,
  nextState: RootState
) => {
  const { origin } = action.meta
  let eventLabel
  let eventAction = EVENT_ACTIONS.SIGNUP_FOR_PLUS_CTA
  let eventCategory = EVENT_CATEGORY.PAPERCHECKER_FUNNEL
  switch (origin) {
    case EC_UPGRADE_PAGE_ID: {
      eventLabel = EVENT_LABELS.EXPERT_CHECK
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS
      break
    }
    case PAPER_UPLOADED_NON_PLUS_MODAL: {
      eventLabel = EVENT_LABELS.PAPER_PROCESSED_MODAL
      break
    }
    case UPGRADE_TO_RECHECK: {
      eventLabel = EVENT_LABELS.UPGRADE_TO_RECHECK
      break
    }
    case PLAGIARISM_LOCK: {
      eventLabel = EVENT_LABELS.PLAGIARISM_LOCK
      break
    }
    case GRAMMAR_LOCK: {
      eventLabel = EVENT_LABELS.GRAMMAR_LOCK
      break
    }
    case FREE_GRAMMAR_EMPTY_STATE: {
      eventLabel = EVENT_LABELS.FREE_GRAMMAR_EMPTY_STATE
      break
    }
    case MORE_ACTIONS_MENU_CREATE_ACCOUNT: {
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS
      eventLabel = EVENT_LABELS.CREATE_ACCOUNT_NAV_LINK
      eventAction = EVENT_ACTIONS.SIGNUP_FOR_FREE_CTA
      break
    }
    case MORE_ACTIONS_MENU_SAVE: {
      eventAction = EVENT_ACTIONS.SIGNUP_FOR_FREE_CTA
      eventLabel = EVENT_LABELS.SAVE_NAV_LINK
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS
      break
    }
    case UPGRADE_TO_EASYBIB_PLUS: {
      eventAction = EVENT_ACTIONS.BETTER_WRITING_PLUS_SIGN_UP
      eventLabel = EVENT_LABELS.PAYMENT_TIERS_INTERSTITIAL
      eventCategory = EVENT_CATEGORY.PAPERCHECKER_FUNNEL
      break
    }
    default: {
      eventLabel = EVENT_LABELS.PAPER_PROCESSED_MODAL
    }
  }
  if (origin === PAPER_UPLOADED_NON_PLUS_MODAL || origin === PLAGIARISM_LOCK) {
    return {
      event: EVENT_TYPES.EVENT,
      eventAction,
      eventCategory,
      eventLabel,
      ...getGTMData(prevState),
      ...getFreemiumPlagData(prevState),
    }
  }
  return {
    event: EVENT_TYPES.EVENT,
    eventAction,
    eventCategory,
    eventLabel,
    ...getGTMData(prevState),
  }
}

export const updateCardEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { ctaText } = action.meta as CardActionsMeta
  const { type, newState } = action.payload as CardActionsPayload
  if (newState !== 'accepted' && newState !== 'rejected') {
    return
  }
  let eventAction = EVENT_ACTIONS.SUGGESTION_CARDS_GRAMMAR_AND_SPELLING
  if (type === 'plagiarism') {
    eventAction = EVENT_ACTIONS.SUGGESTION_CARDS_CITATIONS
  }

  return {
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventLabel: ctaText,
    eventAction,
    ...getGTMData(prevState),
  }
}

export const setActiveEntityKeyEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { isClick, type } = action.meta as ActiveEntityKeyMeta
  if (!isClick) {
    return
  }
  const eventAction =
    type === 'plagiarism'
      ? EVENT_ACTIONS.SUGGESTION_CARDS_CITATIONS
      : EVENT_ACTIONS.SUGGESTION_CARDS_GRAMMAR_AND_SPELLING
  return {
    event: EVENT_TYPES.EVENT,
    eventAction,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventLabel: EVENT_LABELS.CLICK_ON_ERROR_TEXT,
    ...getGTMData(prevState),
  }
}

export const checkErrorEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { type } = action.payload as CheckResultsError
  return {
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ERROR,
    eventAction: EVENT_ACTIONS.CHECK_FAILURE,
    eventLabel:
      type === 'plagiarism' ? EVENT_LABELS.PLAGIARISM_CHECK : EVENT_LABELS.GRAMMAR_CHECK,
    ...getGTMData(prevState),
  }
}

export const deletePaperEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  return {
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventAction: EVENT_ACTIONS.DELETE_PAPER,
    eventLabel: EVENT_LABELS.MY_PAPERS_PAGE,
    ...getGTMData(prevState),
  }
}

export const openExternalLinkEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { source, additionalData } = action.meta as ExternalLinkMeta

  if (!source) {
    return
  }

  switch (source) {
    case PLAGIARISM_LINK_SOURCE: {
      return {
        event: EVENT_TYPES.EVENT,
        eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
        eventLabel: EVENT_LABELS.LINK_OUT,
        eventAction: EVENT_ACTIONS.VIEW_PLAGIARISM_SOURCE,
      }
    }
    case OWL_LINK_SOURCE: {
      if (additionalData) {
        const { rule } = additionalData as OwlLinkAdditionalData
        return {
          event: EVENT_TYPES.EVENT,
          eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
          eventLabel: `${EVENT_LABELS.GRAMMAR_OWL_LINK} - ${rule}`,
          eventAction: EVENT_ACTIONS.CLICK_GRAMMAR_OWL_LINK,
        }
      }
    }
    default: {
      return
    }
  }
}

export const copyPaperEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventLabel: EVENT_LABELS.NAV_LINK,
    eventAction: EVENT_ACTIONS.COPY_PAPER,
  }
}

export const downloadPaperEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventLabel: EVENT_LABELS.NAV_LINK,
    eventAction: EVENT_ACTIONS.DOWNLOAD_PAPER,
  }
}

export const categoryChangeEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { checkCategory } = action.payload as SetCurrentCategoryPayload
  let eventAction
  switch (checkCategory) {
    case GRAMMAR: {
      eventAction = EVENT_ACTIONS.VIEW_GRAMMAR
      break
    }
    case PLAGIARISM: {
      eventAction = EVENT_ACTIONS.VIEW_PLAGIARISM
      break
    }
    default:
      return
  }
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventLabel: EVENT_LABELS.NAV_LINK,
    eventAction,
  }
}

export const hardRedirectEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { label } = action.meta
  switch (label) {
    case UPGRADE_TO_EASBIB_STUDY_PLUS: {
      return {
        ...getGTMData(prevState),
        event: EVENT_TYPES.EVENT,
        eventAction: EVENT_ACTIONS.BEST_WRITING_PLUS_SIGN_UP,
        eventLabel: EVENT_LABELS.WRITING_PAYMENT_TIERS_INTERSTITIAL,
        eventCategory: EVENT_CATEGORY.OUTBOUND_LINK,
      }
    }
    default: {
      return {
        ...getGTMData(prevState),
        event: EVENT_TYPES.EVENT,
        eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
        eventLabel: EVENT_LABELS.NAV_LINK,
        eventAction: label,
      }
    }
  }
}

export const seeSourcesEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventLabel: EVENT_LABELS.SEE_SOURCES,
    eventAction: EVENT_ACTIONS.SUGGESTION_CARDS_CITATIONS,
  }
}

export const textCopiedEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventAction: EVENT_ACTIONS.SELECT_COPY_PAPER,
    eventLabel: EVENT_LABELS.EDITOR_SELECT_COPY,
  }
}

export const localeSwitcherEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
) => {
  const { type } = action.payload as SwitchLocalePayload
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
    eventAction: EVENT_ACTIONS.LANGUAGE_SWITCHER,
    eventLabel:
      type === EN_GB
        ? EVENT_LABELS.LANGUAGE_SWTICHER_UK
        : EVENT_LABELS.LANGUAGE_SWITCHER_US,
  }
}

export const onTopNavHeaderClickEvent: EventDefinition = (
  action,
  prevState: RootState
) => {
  const { label } = action.payload as ClickNavLinkPayload
  return {
    ...getGTMData(prevState),
    event: EVENT_TYPES.EVENT,
    eventCategory: EVENT_CATEGORY.NAVIGATION_BAR,
    eventAction: EVENT_ACTIONS.CLICK_TOP_HEADER_NAV_LINK,
    eventLabel: label?.toLowerCase(),
  }
}

export const userInteractEvent: EventDefinition = (action, prevState: RootState) => {
  const { origin, element } = action.payload as ClickstreamInteractionPayload

  switch (true) {
    case origin === GRAMMAR_CARD_CTA_ORIGIN && element === EXPERT_CHECK_CTA_ELEMENT:
      return {
        ...getGTMData(prevState),
        event: EVENT_TYPES.EVENT,
        eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
        eventAction: EVENT_ACTIONS.CLICK_GRAMMAR_CARD_CTA,
        eventLabel: EVENT_LABELS.GRAMMAR_CARD_TO_EXPERT_CHECK_CTA,
      }
    case origin === UNSUPPORTED_CLAIMS_EC_ORIGIN:
      return {
        ...getGTMData(prevState),
        event: EVENT_TYPES.EVENT,
        eventCategory: EVENT_CATEGORY.PAPERCHECKER_ENGAGEMENTS,
        eventAction: EVENT_ACTIONS.CLICK_UNSUPPORTED_CLAIMS_CTA,
        eventLabel: EVENT_LABELS.UNSUPPORTED_CLAIMS,
      }
    default:
      return
  }
}



// WEBPACK FOOTER //
// ./src/state/gtm/definitions/index.ts